import { useCallback, useEffect, useRef, useState } from "react";
import { renderMarkdown } from "./markdown";
import {
  defaultTransport,
  deleteSession,
  fetchDiagnoseCapability,
  fetchModels,
  fetchSessions,
  fetchSessionTurns,
  streamDiagnose,
} from "./stream";
import {
  agentFocus,
  AgentStreamError,
  fetchAgents,
  fetchAgentSessions,
  fetchAgentSessionTurns,
  streamAgentDiagnose,
} from "./agents";
import type {
  AgentDiagnoseEvent,
  AgentSessionSummary,
  AgentSummary,
  DiagnoseTransport,
  K8sdiagCapability,
  K8sdiagEvent,
  K8sdiagFocus,
  K8sdiagModels,
  K8sdiagPageContext,
  K8sdiagSession,
  K8sdiagTurn,
} from "./types";

/**
 * 진단 패널 — k8sdiag ADR-0001·0005의 호스트 쪽 절반입니다.
 *
 * 규칙 넷을 지킵니다.
 *   - **띄울지 판정하는 주체는 진단 서비스입니다.** capability가 없거나
 *     `available`이 거짓이면 아무것도 렌더하지 않습니다. 비활성 버튼으로도
 *     남기지 않습니다 — 누를 수 없는 버튼은 "권한을 요청하면 되는 기능"으로
 *     읽히지만 실제로는 이 배포에서 조건이 성립하지 않는 상태입니다.
 *   - **좌표의 출처는 등록이고, 질문은 그 사본을 나릅니다.** 어느 화면을 보고
 *     있는지는 호스트가 따로 등록하고(`PUT /viewer`), 질문은 같은 값을
 *     `page_context`로 한 번 더 싣습니다 — 등록은 상류 프로세스 안에 TTL로
 *     살아 있어서 재시작·복제본·방금 이동한 직후에 비고, 그때 좌표 없는 질문은
 *     "어느 네임스페이스를 볼까요"로 되돌아옵니다. 순서는 상류가 정합니다:
 *     등록이 있으면 등록이 이깁니다. (k8sdiag ADR-0005 §2) 무엇이 실제로
 *     프롬프트에 실렸는지는 `context_used`가 답변 위 칩 하나로 말합니다 —
 *     인가 집합 밖이라 버려졌으면 그 칩이 없습니다.
 *   - **모델이 쓴 것은 마크업이 아닙니다.** 답변은 마크다운으로만 해석되고 원시
 *     HTML은 엘리먼트가 되지 못합니다. 미리보기도 이미 마스킹된 문자열
 *     그대로입니다.
 *   - **경로를 알지 못합니다.** 요청을 어떻게 보내는지는 `transport` 하나로만
 *     들어옵니다 — 호스트마다 다른 것은 base 경로와 토큰뿐입니다.
 *
 * 배치는 대화창의 것입니다. 세션 고르기처럼 한 번 쓰고 마는 컨트롤은 헤더 메뉴로
 * 접히고, 도구 호출은 접힌 진행 행으로 눕고, 남는 세로는 전부 대화가 씁니다 —
 * 입력창은 스크롤과 무관하게 바닥에 붙어 있습니다.
 */

/** 저장 당시보다 권한이 좁아진 대화는 다시 그릴 수 없습니다 — 이유를 한 줄로 말합니다. */
const NOT_REPLAYABLE = "권한 범위가 바뀌어 복원할 수 없습니다";
/* 상류가 아직 replayable을 말하지 않아도 턴이 하나도 오지 않으면 되살릴 수 없는
   대화입니다. 이때 초기 화면("무엇을 진단할까요?")을 그대로 두면 고른 적이 없는
   것과 구별되지 않습니다 — 사라졌다는 사실과 그 이유를 대화 자리에서 말합니다. */
const GONE_TITLE = "이 대화는 더 볼 수 없습니다";
const GONE_REASON = "보존 기간이 지났거나 권한 범위가 바뀌었습니다";

/**
 * 페이지 공유 토글 — 상태는 호스트가 갖습니다. 패널이 닫혀 있는 동안에도 공유가
 * 유지돼야 하므로 위젯이 소유하지 않고, 위젯은 라벨과 값을 받아 그리기만 합니다.
 */
export interface DiagnoseShare {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}

export interface DiagnosePanelProps {
  /** 화면에 붙어 있는 채로 접힙니다 — 닫아도 대화가 사라지지 않습니다. */
  open: boolean;
  onClose: () => void;
  /** 판정을 이 네임스페이스 하나로 좁힙니다. 화면이 아는 값입니다. */
  namespace?: string;
  /** 없으면 메뉴에 토글이 나오지 않습니다 — 공유 경로가 없는 호스트입니다. */
  share?: DiagnoseShare;
  /**
   * 지금 보고 있는 화면. 등록(`PUT /viewer`)과 **같은 값**을 질문에도 실어
   * 보냅니다 — 등록은 상류 프로세스 안에 TTL로 살아 있어서 재시작·복제본·방금
   * 이동한 직후에 비고, 그때 질문이 좌표 없이 도착합니다. 호스트가 공유를 꺼
   * 두면 `null`이며 그때는 어느 경로로도 나가지 않습니다. (k8sdiag ADR-0005 §2)
   */
  focus?: K8sdiagFocus | null;
  transport?: DiagnoseTransport;
  /**
   * 에이전트 상류의 통로입니다. (ADR 0043 결정 3 · ADR 0045)
   *
   * 없으면 엔진 선택이 **아예 없습니다** — 고를 수 없는 컨트롤을 비활성으로
   * 남기지 않습니다. 배포에 두 주소가 없으면 목록이 비고, 그때도 같습니다.
   * 패널을 띄울지 판정하는 주체는 여전히 진단 서비스 하나입니다.
   */
  agentTransport?: DiagnoseTransport;
}

interface TraceEntry {
  key: number;
  tool: string;
  args: string;
  scope: string[];
  bytes?: number;
  preview?: string;
  truncated?: boolean;
  /**
   * 에이전트 진행의 신원입니다. 도구는 병렬로 돌 수 있으므로 완료를 **마지막
   * 항목**에 붙이면 다른 도구의 완료가 됩니다. (ADR 0045 결정 6)
   */
  id?: string;
  phase?: string;
  status?: string;
  elapsedMs?: number;
}

interface ScopeBadge {
  text: string;
  title: string;
}

interface Turn {
  key: number;
  question: string;
  answer: string;
  focus: K8sdiagFocus | null;
  scope: ScopeBadge | null;
  trace: TraceEntry[];
  failure: string;
  /**
   * 이 턴을 쓴 쪽입니다. **셀렉터가 아니라 턴이 출처를 갖습니다** — 엔진을 바꾼
   * 뒤에도 앞선 답이 누구의 것인지 화면이 말할 수 있어야 합니다.
   * 에이전트 답변에는 k8sdiag의 근거 보증을 붙이지 않습니다. (ADR 0043 결과)
   */
  source: "k8sdiag" | "agent";
  sourceLabel: string;
  /** 도구 승인 요청이 왔고, 이 패널에는 승인 경로가 없습니다. */
  approval: boolean;
  /**
   * 이 턴이 속한 k8sdiag 대화입니다. 이어서 묻기가 넘기는 값의 출처는 **이
   * 턴**이지 지금 고른 대화가 아닙니다 — 그렇지 않으면 "새 대화" 뒤에 남은 옛
   * 식별자가 조용히 따라갑니다. 에이전트 턴에는 없습니다.
   */
  k8sdiagSessionId: string;
}

/** 새 턴 하나입니다 — 출처는 만들 때 정해지고 뒤에 바뀌지 않습니다. */
function newTurn(key: number, question: string, source: "k8sdiag" | "agent", sourceLabel: string): Turn {
  return { key, question, answer: "", focus: null, scope: null, trace: [], failure: "", source, sourceLabel, approval: false, k8sdiagSessionId: "" };
}

/** 여러 줄 질문은 받되, 입력창이 대화를 밀어내지는 않게 하는 상한입니다. */
const COMPOSER_MAX_HEIGHT = 160;

/**
 * 좌표를 질문 본문이 받는 형태로 옮깁니다. 네임스페이스가 없는 화면(클러스터
 * 전체)은 옮기지 않습니다 — 상류가 대조할 값이 없어 어차피 버리고, 열거에도
 * 없는 `kind`라 요청 전체가 422가 됩니다. (k8sdiag ADR-0005 §1)
 */
function pageContext(focus: K8sdiagFocus | null | undefined): K8sdiagPageContext | undefined {
  if (!focus || focus.kind === "cluster" || !focus.namespace) return undefined;
  return {
    kind: focus.kind,
    namespace: focus.namespace,
    ...(focus.cluster_id ? { cluster_id: focus.cluster_id } : {}),
    ...(focus.workload_kind ? { workload_kind: focus.workload_kind } : {}),
    ...(focus.name ? { name: focus.name } : {}),
  };
}

function focusLabel(focus: K8sdiagFocus): string {
  const object = focus.name ? `${focus.workload_kind ?? focus.kind}/${focus.name}` : "";
  return [focus.namespace, object].filter(Boolean).join(" · ") || focus.kind;
}

/** 답변이 어느 범위로 제한됐는지 한 줄로 말합니다 — 범위를 모르면 없습니다. */
function scopeBadge(data: { scope?: string[]; namespace_count?: number; is_admin?: boolean }): ScopeBadge | null {
  if (data.is_admin) return { text: "전체 클러스터", title: "" };
  if (typeof data.namespace_count !== "number") return null;
  return { text: `네임스페이스 ${data.namespace_count}개`, title: (data.scope ?? []).join(", ") };
}

function replay(history: K8sdiagTurn[], source: "k8sdiag" | "agent" = "k8sdiag", sourceLabel = "", sessionId = ""): Turn[] {
  const restored: Turn[] = [];
  for (const turn of history) {
    const last = restored[restored.length - 1];
    if (turn.role === "assistant" && last && !last.answer) {
      last.answer = turn.content;
      continue;
    }
    const entry = newTurn(restored.length, turn.role === "user" ? turn.content : "", source, sourceLabel);
    entry.k8sdiagSessionId = source === "k8sdiag" ? sessionId : "";
    if (turn.role === "assistant") entry.answer = turn.content;
    restored.push(entry);
  }
  return restored;
}

/**
 * 에이전트 실패의 **고정 문구**입니다. (ADR 0043 결과 — 패널은 503·타임아웃·형식
 * 불일치를 구분해 보여 줘야 하고, 상류의 `use` 거절은 거절로 남아야 합니다)
 *
 * 키는 BFF가 정한 안정 사유 코드입니다. 상류가 보낸 원문 메시지는 쓰지 않습니다 —
 * 그 문자열은 내부 주소·질의를 담을 수 있고, 화면의 문장은 배포 구성을 설명하는
 * 자리가 아닙니다. 모르는 코드는 일반 문구 하나로 모읍니다.
 */
const AGENT_FAILURE: Record<string, string> = {
  unauthorized: "이 에이전트를 쓸 자격이 확인되지 않았습니다. 다시 로그인한 뒤 시도해 주세요.",
  forbidden: "이 에이전트를 쓸 권한이 없습니다.",
  agent_not_available: "고른 에이전트를 더 이상 쓸 수 없습니다.",
  agent_not_found: "고른 에이전트를 찾지 못했습니다.",
  agents_not_configured: "이 배포에는 진단 에이전트가 설정되어 있지 않습니다.",
  agents_unavailable: "에이전트 서비스에 연결하지 못했습니다.",
  agent_session_busy: "이 대화에 이미 진행 중인 질문이 있습니다. 끝난 뒤 다시 시도해 주세요.",
  agent_rate_limited: "요청이 한도를 넘었습니다. 잠시 후 다시 시도해 주세요.",
  stream_capacity: "지금은 동시에 열 수 있는 대화 수를 넘었습니다. 잠시 후 다시 시도해 주세요.",
  agent_timeout: "에이전트가 시간 안에 답하지 않았습니다.",
  agent_stream_malformed: "에이전트 응답이 계약과 달라 표시하지 않았습니다.",
  agent_stream_truncated: "에이전트 응답이 끝나기 전에 끊겼습니다.",
  agent_stream_too_large: "에이전트 응답이 너무 커서 표시하지 않았습니다.",
  agent_empty_response: "에이전트가 답을 돌려주지 않았습니다.",
  agent_failed: "에이전트가 답을 마치지 못했습니다.",
};

/** HTTP 상태로만 거절된 요청을 같은 사유 표로 옮깁니다. */
const AGENT_REJECTED: Record<string, string> = {
  "400": "agent_stream_malformed",
  "401": "unauthorized",
  "403": "forbidden",
  "404": "agent_not_found",
  "409": "agent_session_busy",
  "413": "agent_stream_too_large",
  "429": "agent_rate_limited",
  "502": "agents_unavailable",
  "503": "agents_not_configured",
  "504": "agent_timeout",
};

const AGENT_FAILURE_FALLBACK = "에이전트가 답을 마치지 못했습니다. 잠시 후 다시 시도해 주세요.";

/** 실패 하나를 화면 문장 하나로 옮깁니다. 모르는 사유는 일반 문구입니다. */
function agentFailureText(reason: unknown): string {
  const code = reason instanceof AgentStreamError ? reason.code : typeof reason === "string" ? reason : "";
  const rejected = code.startsWith("agent_rejected_") ? AGENT_REJECTED[code.slice("agent_rejected_".length)] : "";
  return AGENT_FAILURE[rejected || code] ?? AGENT_FAILURE_FALLBACK;
}

/** 접힌 채로도 그 호출이 무엇을 얼마나 읽었는지 한 줄로 말합니다. */
function stepSummary(entry: TraceEntry): string {
  /* 에이전트 진행에는 "이만큼 읽었다"가 없습니다 — 상류가 말하지 않은 값을
     지어내지 않고, 실제로 온 단계와 결과만 말합니다. (ADR 0045 결정 6) */
  if (entry.phase) {
    const parts = [entry.phase === "end" ? (entry.status || "완료") : "실행 중"];
    if (entry.elapsedMs !== undefined && entry.phase === "end") parts.push(`${Math.round(entry.elapsedMs)} ms`);
    return parts.join(" · ");
  }
  const parts = [`인가된 범위: ${entry.scope.length ? entry.scope.join(", ") : "없음"}`];
  if (entry.bytes !== undefined) parts.push(`${entry.bytes} bytes`);
  if (entry.truncated) parts.push("미리보기 일부");
  return parts.join(" · ");
}

export function DiagnosePanel({
  open,
  onClose,
  namespace,
  share,
  focus,
  transport = defaultTransport,
  agentTransport,
}: DiagnosePanelProps) {
  const [capability, setCapability] = useState<K8sdiagCapability | null>(null);
  const [sessions, setSessions] = useState<K8sdiagSession[]>([]);
  const [session, setSession] = useState("");
  const [models, setModels] = useState<K8sdiagModels | null>(null);
  const [model, setModel] = useState("");
  const [turns, setTurns] = useState<Turn[]>([]);
  /** 열어 보니 비어 있던 대화들입니다 — 목록에서도 복원 불가로 내립니다. */
  const [gone, setGone] = useState<string[]>([]);
  const [question, setQuestion] = useState("");
  const [running, setRunning] = useState(false);
  /* 엔진은 k8sdiag("") 또는 에이전트 id 하나입니다. 모델 선택과 **같은 줄에 두지
     않습니다** — 모델은 k8sdiag가 레지스트리에서 받은 목록이고 에이전트는 그
     자체가 모델을 갖습니다. 한 컨트롤로 섞으면 고른 것이 무엇인지 화면이 설명할
     수 없습니다. (ADR 0043 결정 3) */
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [engine, setEngine] = useState("");
  const [agentSessions, setAgentSessions] = useState<AgentSessionSummary[]>([]);
  /**
   * 대화 **밖**의 실패입니다. 턴으로 남기면 그 다음 진단 질문이 같은 목록에 붙어
   * 두 상류의 이력이 한 화면에 섞입니다 — ADR 0043은 둘을 섞지 말라고 합니다.
   */
  const [notice, setNotice] = useState("");
  const abort = useRef<AbortController>();
  const picked = useRef("");
  /* 대화가 바뀔 때마다 오릅니다. 앞선 스트림의 이벤트가 **새로 고른 대화**를
     고치지 못하게 하는 표식입니다 — 키가 달라 패치가 빗나가더라도 세션 id와
     진행 표시는 여전히 늦게 도착할 수 있습니다. */
  const conversation = useRef(0);
  const menu = useRef<HTMLDetailsElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const composer = useRef<HTMLTextAreaElement>(null);

  /* 판정은 화면의 네임스페이스로 좁혀 묻습니다 — 인가 집합 어딘가에 권한이
     있다고 지금 보고 있는 네임스페이스를 진단할 수 있는 것은 아닙니다. */
  useEffect(() => {
    if (!open) return;
    const controller = new AbortController();
    void fetchDiagnoseCapability(transport, controller.signal, namespace).then(setCapability);
    return () => controller.abort();
  }, [open, namespace, transport]);

  const reloadSessions = useCallback(() => {
    const controller = new AbortController();
    void fetchSessions(transport, controller.signal).then(setSessions);
    return () => controller.abort();
  }, [transport]);

  useEffect(() => {
    if (!open || !capability?.available) return;
    return reloadSessions();
  }, [open, capability?.available, reloadSessions]);

  useEffect(() => {
    if (!open || !capability?.available) return;
    const controller = new AbortController();
    void fetchModels(transport, controller.signal).then((value) => {
      setModels(value);
      setModel(value?.default ?? "");
    });
    return () => controller.abort();
  }, [open, capability?.available, transport]);

  /* 진단에 쓸 수 있는 에이전트입니다. 통로가 없거나 목록이 비면 엔진 선택 자체가
     그려지지 않습니다 — 비활성 컨트롤로 남기지 않습니다. (ADR 0043 결정 3) */
  useEffect(() => {
    if (!open || !capability?.available || !agentTransport) return;
    const controller = new AbortController();
    /* 취소를 존중하지 않는 전송도 있습니다 — 그때 늦게 도착한 목록이 이미 정리된
       화면을 되살리지 못하도록 여기서 한 번 더 막습니다. */
    let stale = false;
    void fetchAgents(agentTransport, controller.signal).then((value) => {
      if (!stale) setAgents(value);
    });
    return () => {
      stale = true;
      controller.abort();
    };
  }, [open, capability?.available, agentTransport]);

  /* 고른 에이전트가 목록에서 사라졌습니다(권한 회수·opt-in 해제·중단). 진행 중인
     답은 더 이상 이 화면의 것이 아니므로 끊고, 엔진과 대화를 되돌립니다 —
     남겨 두면 다음 질문이 갈 곳 없는 id로 나갑니다. */
  useEffect(() => {
    if (!engine || agents.some((entry) => entry.id === engine)) return;
    abort.current?.abort();
    conversation.current += 1;
    setEngine("");
    setSession("");
    setAgentSessions([]);
    setRunning(false);
    /* 화면에 남은 것은 **에이전트 대화**입니다. 비우지 않으면 다음 진단 질문이
       그 아래에 붙어 두 출처의 이력이 한 목록이 됩니다. */
    picked.current = "";
    setTurns([]);
    setNotice("고른 에이전트를 더 이상 쓸 수 없어 대화를 닫았습니다. 진단 서비스로 돌아왔습니다.");
  }, [agents, engine]);

  /* 고른 에이전트의 대화 목록입니다. k8sdiag 목록과 **섞지 않습니다** — 출처가
     다른 두 목록이고, 한 줄로 합치면 어느 쪽 대화인지 말할 수 없습니다. */
  useEffect(() => {
    if (!open || !engine || !agentTransport) {
      setAgentSessions([]);
      return;
    }
    const controller = new AbortController();
    let stale = false;
    void fetchAgentSessions(agentTransport, engine, controller.signal).then((value) => {
      if (!stale) setAgentSessions(value);
    });
    return () => {
      stale = true;
      controller.abort();
    };
  }, [open, engine, agentTransport]);

  /* 새 턴은 바닥에 붙습니다. scrollTop은 레이아웃을 계산하지 않는 평범한 속성이라
     테스트 환경에서도 렌더 경로가 갈라지지 않습니다. */
  useEffect(() => {
    const el = log.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [turns]);

  /* 입력창은 내용만큼만 자랍니다. 상한을 넘으면 스스로 스크롤합니다. */
  useEffect(() => {
    const el = composer.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT)}px`;
  }, [question]);

  /* 패널이 사라지면 상류 실행도 끊습니다. */
  useEffect(() => () => abort.current?.abort(), []);

  if (!open || !capability?.available) return null;

  const samples = capability.sample_questions ?? [];
  /* 지금 답할 쪽입니다. 없으면 진단 서비스입니다. */
  const selected = engine ? agents.find((entry) => entry.id === engine) : undefined;

  const apply = (key: number, event: K8sdiagEvent) => {
    const patch = (fn: (turn: Turn) => Turn) =>
      setTurns((prev) => prev.map((turn) => (turn.key === key ? fn(turn) : turn)));
    if (event.event === "context_used") {
      patch((turn) => ({ ...turn, focus: event.data.focus, scope: scopeBadge(event.data) }));
      return;
    }
    if (event.event === "tool_call") {
      const { tool, arguments: args, scope } = event.data;
      patch((turn) => ({
        ...turn,
        trace: [...turn.trace, { key: turn.trace.length, tool, args: JSON.stringify(args ?? {}), scope: scope ?? [] }],
      }));
      return;
    }
    if (event.event === "tool_result") {
      const { bytes, preview, truncated } = event.data;
      patch((turn) => ({
        ...turn,
        trace: turn.trace.map((entry, index) =>
          index === turn.trace.length - 1 ? { ...entry, bytes, preview, truncated } : entry,
        ),
      }));
      return;
    }
    if (event.event === "message") {
      patch((turn) => ({ ...turn, answer: event.data.answer }));
      return;
    }
    /* 스트림이 시작된 뒤의 실패는 HTTP 상태가 아니라 이 이벤트로 옵니다. */
    patch((turn) => ({ ...turn, failure: "진단을 마치지 못했습니다. 잠시 후 다시 시도해 주세요." }));
  };

  /**
   * 에이전트 스트림을 턴 하나에 붙입니다.
   *
   * k8sdiag와 **다른 계약**입니다. 답변은 조각으로 오므로 이어 붙이고, 도구
   * 진행은 id가 신원이라 그 id의 행을 고칩니다 — 마지막 행에 완료를 붙이면 다른
   * 도구의 완료가 됩니다. 승인 요청은 실행 결과가 아니라 "이 패널에는 승인
   * 경로가 없다"는 표시입니다. (ADR 0045 결정 6)
   */
  const applyAgent = (key: number, event: AgentDiagnoseEvent) => {
    const patch = (fn: (turn: Turn) => Turn) =>
      setTurns((prev) => prev.map((turn) => (turn.key === key ? fn(turn) : turn)));
    if (event.event === "context_used") {
      patch((turn) => ({ ...turn, focus: event.data.focus as K8sdiagFocus | null }));
      return;
    }
    if (event.event === "message_delta") {
      patch((turn) => ({ ...turn, answer: turn.answer + event.data.delta }));
      return;
    }
    if (event.event === "tool_progress") {
      const { id, name, phase, status, elapsed_ms: elapsedMs } = event.data;
      patch((turn) => {
        const index = turn.trace.findIndex((entry) => entry.id === id);
        const merged: TraceEntry =
          index >= 0
            ? { ...turn.trace[index], phase, status, elapsedMs }
            : { key: turn.trace.length, id, tool: name, args: "", scope: [], phase, status, elapsedMs };
        const trace = index >= 0 ? turn.trace.map((entry, i) => (i === index ? merged : entry)) : [...turn.trace, merged];
        return { ...turn, trace };
      });
      return;
    }
    if (event.event === "approval_required") {
      patch((turn) => ({ ...turn, approval: true }));
      return;
    }
    /* 남은 것은 `error` 하나입니다. 사유를 그대로 문장으로 옮깁니다 — 모든 실패를
       "연결하지 못했습니다"로 모으면 권한 거절과 형식 불일치가 같은 화면이 됩니다. */
    const reason = event.event === "error" ? event.data.error : "";
    patch((turn) => ({ ...turn, failure: agentFailureText(reason) }));
  };

  /**
   * 질문 하나를 보냅니다.
   *
   * `handoff`가 있으면 그 에이전트로 **한 번만** 나갑니다 — 이어서 묻기의 첫
   * 요청이고, 원래 진단 대화의 식별자는 그 요청에만 실립니다. 두 번째 질문부터는
   * 평범한 에이전트 대화이므로 그 값이 따라가지 않습니다. (ADR 0043 결정 2)
   */
  const send = async (asked: string, handoff?: { agentId: string; k8sdiagSessionId: string }) => {
    if (!asked || running) return;
    /* 보내는 순간의 화면입니다 — 등록이 비어 있을 때 상류가 읽는 폴백입니다. */
    const screen = pageContext(focus);
    abort.current?.abort();
    const controller = new AbortController();
    abort.current = controller;
    const key = Date.now();
    const target = handoff ? handoff.agentId : engine;
    const agent = target ? agents.find((entry) => entry.id === target) : undefined;
    /* **fail-closed.** 고른 엔진이 에이전트인데 그 에이전트가 목록에 없으면
       진단 서비스로 되돌아가지 않습니다 — 되돌아가면 사용자가 고르지 않은
       상류가 답하고, 두 대화의 세션 식별자가 한 턴에 섞입니다. */
    if (target && !agent) {
      /* 실패를 턴으로 남기지 않습니다 — 남기면 다음 진단 질문이 에이전트 대화
         아래에 붙습니다. 대화는 닫고 사유는 대화 밖 한 줄로 말합니다. */
      conversation.current += 1;
      setEngine("");
      setSession("");
      setAgentSessions([]);
      picked.current = "";
      setTurns([]);
      setRunning(false);
      setNotice("고른 에이전트를 더 이상 쓸 수 없습니다. 목록에서 다시 골라 주세요.");
      return;
    }
    setNotice("");
    const turn = agent ? newTurn(key, asked, "agent", agent.name) : newTurn(key, asked, "k8sdiag", "");
    /* 이 질문이 속한 대화입니다. 답이 오는 동안 엔진이나 대화가 바뀌면 그 답은
       더 이상 이 화면의 것이 아닙니다. */
    const round = conversation.current;
    setTurns((prev) => [...prev, turn]);
    setQuestion("");
    setRunning(true);
    try {
      const id = agent
        ? await streamAgentDiagnose(
            agentTransport!,
            agent.id,
            {
              question: asked,
              /* 이어받기는 **새 대화**를 엽니다 — 두 상류는 세션이 다르므로 진단
                 대화의 식별자를 에이전트 대화 키로 쓰면 서로의 대화가 섞입니다. */
              session_id: handoff ? null : session || null,
              /* 넘기는 것은 셋뿐입니다 — 검증될 좌표, 원래 진단 세션 id, 질문.
                 답변 본문·도구 궤적·미리보기는 넘기지 않습니다. 이어받기가 아닌
                 질문에는 이 값이 없습니다. */
              k8sdiag_session_id: handoff?.k8sdiagSessionId || null,
              ...(focus ? { focus: agentFocus(focus) } : {}),
            },
            (event) => {
              if (conversation.current === round) applyAgent(key, event);
            },
            controller.signal,
          )
        : await streamDiagnose(
            transport,
            {
              question: asked,
              session_id: session || null,
              ...(model ? { model } : {}),
              ...(screen ? { page_context: screen } : {}),
            },
            (event) => {
              if (conversation.current === round) apply(key, event);
            },
            controller.signal,
          );
      if (conversation.current !== round) return;
      if (id) {
        setSession(id);
        picked.current = id;
        /* 이 턴이 어느 대화에 속하는지 여기서 확정합니다 — 이어서 묻기가 읽는
           값의 출처입니다. */
        if (!agent) setTurns((prev) => prev.map((entry) => (entry.key === key ? { ...entry, k8sdiagSessionId: id } : entry)));
      }
      if (agent) {
        void fetchAgentSessions(agentTransport!, agent.id, controller.signal).then((value) => {
          if (conversation.current === round) setAgentSessions(value);
        });
      } else {
        reloadSessions();
      }
    } catch (error) {
      if (!controller.signal.aborted && conversation.current === round) {
        const failure = agent ? agentFailureText(error) : "진단 서비스에 연결하지 못했습니다.";
        setTurns((prev) => prev.map((entry) => (entry.key === key ? { ...entry, failure } : entry)));
      }
    } finally {
      if (!controller.signal.aborted && conversation.current === round) setRunning(false);
    }
  };

  /**
   * **에이전트에게 이어서 묻기.** (ADR 0043 결정 2)
   *
   * 넘기는 것은 셋뿐입니다 — 사용자가 쓴 **원래 질문**, 그 답이 속한 k8sdiag
   * 대화의 식별자, 그리고 지금 공유 중인(서버가 검증할) 화면 좌표. 답변 본문과
   * 도구 궤적·미리보기는 넘기지 않습니다: 마스킹된 문자열을 다른 저장소로
   * 복사하는 경로를 만들지 않습니다.
   *
   * 대화는 **새로 엽니다.** 두 상류는 세션이 다르므로 이어받을 수 없습니다.
   */
  const handoff = (agentId: string, turn: Turn) => {
    abort.current?.abort();
    conversation.current += 1;
    setEngine(agentId);
    setSession("");
    picked.current = "";
    setTurns([]);
    setNotice("");
    setRunning(false);
    menu.current?.removeAttribute("open");
    void send(turn.question, { agentId, k8sdiagSessionId: turn.k8sdiagSessionId });
  };

  /**
   * 엔진을 바꿉니다. 진행 중인 답은 끊고 대화는 새로 시작합니다 — 두 상류는
   * 세션이 다르므로 같은 대화를 이어받을 수 없습니다.
   */
  const pickEngine = (next: string) => {
    if (next === engine) return;
    abort.current?.abort();
    conversation.current += 1;
    setEngine(next);
    setSession("");
    picked.current = "";
    setTurns([]);
    setRunning(false);
    setNotice("");
  };

  /* Enter로 보내고 Shift+Enter로 줄을 바꿉니다. IME 조합을 확정하는 Enter도 같은
     키 이벤트로 오므로, 그것까지 전송으로 읽으면 한글은 첫 음절만 나갑니다. */
  const onKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key !== "Enter" || e.shiftKey || e.nativeEvent.isComposing) return;
    e.preventDefault();
    void send(question.trim());
  };

  const pick = (id: string) => {
    /* 진행 중인 답은 새로 고른 대화의 것이 아닙니다 — 끊고, 늦게 오는 이벤트가
       이 대화를 고치지 못하게 표식을 올립니다. */
    abort.current?.abort();
    conversation.current += 1;
    setRunning(false);
    setSession(id);
    setTurns([]);
    setNotice("");
    picked.current = id;
    menu.current?.removeAttribute("open");
    if (!id) return;
    const round = conversation.current;
    const agent = agents.find((entry) => entry.id === engine);
    if (agent && agentTransport) {
      void fetchAgentSessionTurns(agentTransport, agent.id, id).then((history) => {
        if (picked.current !== id || conversation.current !== round) return;
        if (history.length === 0) {
          setGone((prev) => (prev.includes(id) ? prev : [...prev, id]));
          return;
        }
        setTurns(replay(history, "agent", agent.name));
      });
      return;
    }
    void fetchSessionTurns(transport, id).then((history) => {
      if (picked.current !== id || conversation.current !== round) return;
      if (history.length === 0) {
        setGone((prev) => (prev.includes(id) ? prev : [...prev, id]));
        return;
      }
      setTurns(replay(history, "k8sdiag", "", id));
    });
  };

  const forget = async (id: string) => {
    await deleteSession(transport, id);
    setSessions((prev) => prev.filter((entry) => entry.session_id !== id));
    if (id === session) {
      picked.current = "";
      setSession("");
      setTurns([]);
    }
    menu.current?.removeAttribute("open");
    reloadSessions();
  };

  return (
    <aside id="diagnose-panel" className="diagnose-panel" aria-label="진단">
      <header className="diagnose-panel__head">
        <strong className="diagnose-panel__title">진단</strong>
        <div className="diagnose-panel__actions">
          {/* 세션은 한 번 고르면 다시 볼 일이 없는 컨트롤입니다 — 대화 위에 상시
              띄우는 대신 헤더 메뉴로 접습니다. details가 여닫는 상태와 키보드
              조작을 이미 갖고 있으므로 그 위에 흉내를 얹지 않습니다. */}
          <details className="diagnose-menu" ref={menu}>
            <summary className="diagnose-menu__trigger" aria-label="대화" title="대화">
              ☰
            </summary>
            <div className="diagnose-menu__pop">
              {/* 엔진 선택입니다. 고를 수 있는 에이전트가 하나도 없으면 컨트롤
                  자체가 없습니다 — 비활성 셀렉터는 "권한을 요청하면 되는 기능"으로
                  읽히지만 실제로는 이 배포에 없는 상태입니다. (ADR 0043 결정 3)
                  모델 선택과 **같은 줄에 두지 않습니다**: 모델은 k8sdiag가 받은
                  목록이고 에이전트는 그 자체가 모델을 갖습니다. */}
              {agents.length > 0 && (
                <select
                  className="diagnose-menu__item"
                  aria-label="엔진"
                  value={engine}
                  onChange={(e) => pickEngine(e.target.value)}
                >
                  <option value="">진단 서비스</option>
                  {agents.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.name}
                    </option>
                  ))}
                </select>
              )}
              {engine === "" && (models?.models.length ?? 0) > 1 && (
                <select
                  className="diagnose-menu__item"
                  aria-label="모델"
                  value={model}
                  onChange={(e) => setModel(e.target.value)}
                >
                  {models?.models.map((entry) => (
                    <option key={entry.id} value={entry.id}>
                      {entry.label}
                    </option>
                  ))}
                </select>
              )}
              {share && (
                <label className="diagnose-menu__toggle">
                  <input
                    type="checkbox"
                    checked={share.checked}
                    onChange={(e) => share.onChange(e.target.checked)}
                  />
                  <span>{share.label}</span>
                </label>
              )}
              <button type="button" className="diagnose-menu__item" onClick={() => pick("")}>
                새 대화
              </button>
              {engine !== "" &&
                agentSessions.map((entry) => {
                  /* 에이전트 대화는 k8sdiag 대화와 **다른 목록**입니다. 한 줄로
                     합치면 어느 쪽 대화인지 말할 수 없습니다. 삭제는 이 저장소가
                     소유하지 않으므로 지우기 버튼을 두지 않습니다. */
                  const label = entry.title || entry.preview || entry.session_id;
                  const reason = gone.includes(entry.session_id) ? GONE_REASON : "";
                  return (
                    <div key={entry.session_id} className="diagnose-menu__row">
                      <button
                        type="button"
                        className="diagnose-menu__item"
                        aria-current={entry.session_id === session}
                        aria-disabled={!!reason}
                        aria-label={reason ? `${label} — ${reason}` : undefined}
                        title={reason || undefined}
                        onClick={() => {
                          if (!reason) pick(entry.session_id);
                        }}
                      >
                        {label}
                      </button>
                    </div>
                  );
                })}
              {engine === "" &&
                sessions.map((entry) => {
                const label = entry.question_preview || entry.session_id;
                /* 필드가 없는 상류는 아직 판정을 말하지 않는 것이므로 복원 가능으로 읽되,
                   빈 대화로 확인된 것은 그 자리에서 같은 비활성 표시로 내립니다. */
                const reason = entry.replayable === false ? NOT_REPLAYABLE : gone.includes(entry.session_id) ? GONE_REASON : "";
                const replayable = !reason;
                return (
                  <div key={entry.session_id} className="diagnose-menu__row">
                    <button
                      type="button"
                      className="diagnose-menu__item"
                      aria-current={entry.session_id === session}
                      aria-disabled={!replayable}
                      aria-label={replayable ? undefined : `${label} — ${reason}`}
                      title={replayable ? undefined : reason}
                      onClick={() => {
                        if (replayable) pick(entry.session_id);
                      }}
                    >
                      {label}
                    </button>
                    <button
                      type="button"
                      className="diagnose-menu__forget"
                      aria-label={`대화 삭제: ${label}`}
                      title={`대화 삭제: ${label}`}
                      disabled={!replayable}
                      onClick={() => void forget(entry.session_id)}
                    >
                      ✕
                    </button>
                  </div>
                );
                })}
            </div>
          </details>
          <button type="button" className="diagnose-panel__icon" aria-label="닫기" title="닫기" onClick={onClose}>
            ✕
          </button>
        </div>
      </header>

      <div className="diagnose__log" ref={log} role="log" aria-live="polite" aria-busy={running}>
        {turns.length === 0 && gone.includes(session) ? (
          <div className="diagnose__welcome">
            <p className="diagnose__welcome-title">{GONE_TITLE}</p>
            <p className="diagnose__welcome-desc">{GONE_REASON}</p>
          </div>
        ) : turns.length === 0 && selected ? (
          /* 에이전트 대화의 빈 화면입니다. **k8sdiag의 소개와 예시 질문을 쓰지
             않습니다** — 그 문장은 진단 서비스가 광고하는 고정 도구 집합을
             설명하고, 에이전트의 도구는 manager 쪽에서 따로 구성됩니다. 여기서
             말할 수 있는 것은 누가 답하는가와 그 답에 근거 보증이 없다는 사실뿐입니다. */
          <div className="diagnose__welcome">
            <p className="diagnose__welcome-title">{selected.name}에게 묻습니다</p>
            <p className="diagnose__welcome-desc">
              {selected.description
                ? `${selected.description} — 진단 서비스의 근거 표시가 붙지 않습니다.`
                : "이 에이전트가 직접 답합니다. 진단 서비스의 근거 표시가 붙지 않습니다."}
            </p>
          </div>
        ) : turns.length === 0 ? (
          <div className="diagnose__welcome">
            <p className="diagnose__welcome-title">무엇을 진단할까요?</p>
            {capability.description && <p className="diagnose__welcome-desc">{capability.description}</p>}
            {samples.length > 0 && (
              <div className="diagnose__samples">
                {samples.map((sample) => (
                  <button
                    key={sample}
                    type="button"
                    className="diagnose__sample"
                    disabled={running}
                    onClick={() => void send(sample)}
                  >
                    {sample}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          <ol className="diagnose__turns">
            {turns.map((turn) => (
              <li key={turn.key} className="diagnose__turn">
                <div className="diagnose__asked">
                  <p className="diagnose__question">{turn.question}</p>
                </div>

                {turn.trace.length > 0 && (
                  <div className="diagnose__trace">
                    {turn.trace.map((entry) => (
                      <details key={entry.key} className="diagnose__step">
                        <summary className="diagnose__step-head">
                          <span className="diagnose__step-tool">{entry.tool}</span>
                          <span className="diagnose__step-meta">{stepSummary(entry)}</span>
                        </summary>
                        <div className="diagnose__step-body">
                          {/* 에이전트 진행에는 인자도 결과 미리보기도 오지 않습니다 —
                              상류가 주지 않은 값을 빈 칸으로 흉내 내지 않습니다. */}
                          {entry.args && <code className="diagnose__step-args">{entry.args}</code>}
                          {entry.preview && <pre className="diagnose__preview">{entry.preview}</pre>}
                        </div>
                      </details>
                    ))}
                  </div>
                )}

                {/* 실제로 프롬프트에 실린 좌표만 말합니다 — 버려진 좌표를 되비추면
                    화면이 서버가 하지 않은 일을 했다고 말하게 됩니다. */}
                {turn.scope && (
                  <p className="diagnose__context" title={turn.scope.title || undefined}>
                    {turn.scope.text}
                  </p>
                )}

                {turn.focus && <p className="diagnose__context">이 화면을 근거로 답합니다: {focusLabel(turn.focus)}</p>}

                {/* 출처는 **이 턴의 것**입니다 — 엔진을 바꾼 뒤에도 앞선 답이
                    누구의 것인지 말할 수 있어야 합니다. 에이전트 답변에는
                    k8sdiag의 근거 보증을 붙이지 않습니다. (ADR 0043 결과) */}
                {turn.source === "agent" && (
                  <p className="diagnose__context">
                    이 답은 에이전트가 썼습니다{turn.sourceLabel ? `: ${turn.sourceLabel}` : ""} — 진단 서비스의 근거 표시가 붙지 않습니다.
                  </p>
                )}

                {turn.approval && (
                  <p className="diagnose__failure">
                    에이전트가 도구 실행 승인을 요청했습니다. 이 패널에는 승인 경로가 없어 실행되지 않았습니다.
                  </p>
                )}

                {turn.answer && <div className="diagnose__markdown">{renderMarkdown(turn.answer)}</div>}

                {/* 답이 끝난 진단 턴 **아래**에만 놓입니다. 고를 에이전트가 없으면
                    없고, 여럿이면 어느 에이전트인지 사용자가 고릅니다 — 하나를
                    서버가 골라 주면 어디로 갔는지 화면이 말할 수 없습니다.
                    넘어가는 것은 원래 질문·이 대화의 식별자·공유 중인 좌표뿐입니다. */}
                {turn.source === "k8sdiag" && turn.answer && agents.length > 0 && (
                  <div className="diagnose__samples">
                    {agents.map((entry) => (
                      <button
                        key={entry.id}
                        type="button"
                        className="diagnose__sample"
                        disabled={running}
                        onClick={() => handoff(entry.id, turn)}
                      >
                        {agents.length === 1
                          ? `에이전트에게 이어서 묻기: ${entry.name}`
                          : `이어서 묻기: ${entry.name}`}
                      </button>
                    ))}
                  </div>
                )}

                {turn.failure && <p className="diagnose__failure">{turn.failure}</p>}
              </li>
            ))}
          </ol>
        )}
      </div>

      {/* 대화 밖 안내입니다 — 턴 목록에 넣으면 다음 질문이 그 아래에 붙습니다. */}
      {notice && (
        <p className="diagnose__failure" role="status">
          {notice}
        </p>
      )}

      {/* 스크롤 영역 밖입니다 — 안에 두면 답변이 자라는 동안 같이 떠내려갑니다. */}
      {running && (
        <p className="diagnose__typing">
          <span className="diagnose__dots" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          진단 중…
        </p>
      )}

      <form
        className="diagnose__ask"
        onSubmit={(e) => {
          e.preventDefault();
          void send(question.trim());
        }}
      >
        <textarea
          ref={composer}
          className="diagnose__input"
          aria-label="질문"
          rows={1}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="왜 이 Pod가 준비되지 않나요?"
          maxLength={2000}
          disabled={running}
        />
        <button
          className="diagnose__send"
          type="submit"
          aria-label="진단"
          title="진단"
          disabled={running || !question.trim()}
        >
          ↑
        </button>
      </form>
    </aside>
  );
}
